import React, { useState, useRef, KeyboardEvent, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Flex, Button, Box, useMantineTheme } from '@mantine/core';
import { createStyles } from '@mantine/styles';
import { useHashtagStyling } from './NoteTitlePreprocessing';
import { defaultAiRequestManager } from '@/features/ai/components/McpAI/defaultAiRequestManager';
import { useAuthStore } from '@/stores/authStore';

interface ChatTextInputProps {
    value: string;
    onChange: (value: string) => void;
    onSend: (latestMessage?: string) => void;
    isLoading?: boolean;
    placeholder?: string; // 플레이스홀더 추가
    onSetPlaceholder?: (callback: (placeholder: string) => void) => void; // 플레이스홀더 설정 콜백 등록 함수
    onMessageReceived?: (message: string, isNewMessage?: boolean) => void;
    onToolUseReceived?: (toolUseData: string) => void; // 도구 사용 메시지 콜백 추가
    onResetToolStates?: () => void; // 도구 상태 초기화 함수 추가
}

// Mantine 스타일 정의
const useStyles = createStyles(theme => ({
    chatInput: {
        border: `1px solid ${theme.colors.gray[4]}`,
        borderRadius: theme.radius.md,
        height: '44px',
        padding: '8px 16px',
        outline: 'none',
        lineHeight: 1.5,
        display: 'flex',
        alignItems: 'center',
        overflow: 'auto',
        fontSize: theme.fontSizes.sm,
        width: '100%',

        '&:empty:before': {
            content: 'attr(data-placeholder)',
            color: theme.colors.gray[5],
            pointerEvents: 'none',
        },

        '& .hashtag': {
            color: '#8A7CFF',
            textDecoration: 'underline',
            cursor: 'pointer',
            fontWeight: 500,
        },
    },

    inputContainer: {
        flex: 1,
        position: 'relative',
    },

    hashtagPreview: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        padding: 'inherit',
        pointerEvents: 'none',
        background: 'transparent',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',

        '& .hashtag': {
            color: '#8A7CFF',
            textDecoration: 'underline',
            cursor: 'pointer',
            fontWeight: 500,
        },
    },

    messageContent: {
        '& .hashtag': {
            color: '#8A7CFF',
            textDecoration: 'underline',
            cursor: 'pointer',
        },
    },

    toolResult: {
        '& .hashtag': {
            color: '#8A7CFF',
            textDecoration: 'underline',
            cursor: 'pointer',
        },
    },

    searchNoteHashtag: {
        color: '#8A7CFF',
        textDecoration: 'underline',
        fontWeight: 500,
    },

    sendButton: {
        backgroundColor: theme.colors.blue[5],
        color: theme.white,
        minWidth: '60px',
        borderRadius: theme.radius.md,
        padding: '0 16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '44px',
        marginLeft: theme.spacing.xs,

        '&:hover': {
            backgroundColor: theme.colors.blue[6],
        },
    },
}));

const ChatTextInput: React.FC<ChatTextInputProps> = ({
    value,
    onChange,
    onSend,
    isLoading = false,
    placeholder,
    onSetPlaceholder,
    onMessageReceived,
    onToolUseReceived,
    onResetToolStates,
}) => {
    const { t } = useTranslation('ai');
    // 기본 플레이스홀더를 번역 키로 설정
    const defaultPlaceholder = t('textInput.placeholder');
    // console.log('[TextInput] 기본 플레이스홀더:', defaultPlaceholder);
    const { classes } = useStyles();
    const mantineTheme = useMantineTheme();
    // placeholder가 undefined인 경우 defaultPlaceholder 사용
    const [currentPlaceholder, setCurrentPlaceholder] = useState<string>(placeholder || defaultPlaceholder);
    const inputRef = useRef<HTMLDivElement>(null);
    const [prevValue, setPrevValue] = useState(value);
    const [isComposing, setIsComposing] = useState(false);
    const [isSending, setIsSending] = useState(false);
    // 도구 타입 정의
    interface ChatToolInfo {
        name: string;
        input: Record<string, unknown>;
        isCompleted?: boolean;
        specialType?: string;
        showAfterMessage?: boolean;
    }

    const [activeTools, setActiveTools] = useState<ChatToolInfo[]>([]);
    const { user } = useAuthStore();

    // 해시태그 스타일링 훅 사용
    const { applyHashtagStyling } = useHashtagStyling(inputRef);

    // AI 로딩 상태 이벤트 리스너 추가
    useEffect(() => {
        const handleLoadingStateChanged = (event: Event) => {
            const customEvent = event as CustomEvent<{ isLoading: boolean }>;
            console.log(
                '[TextInput] AI 로딩 상태 변경 이벤트 수신:',
                customEvent.detail.isLoading
            );
            setIsSending(customEvent.detail.isLoading);

            // 로딩이 완료되면 입력 필드에 포커스 설정
            if (!customEvent.detail.isLoading && inputRef.current) {
                setTimeout(() => {
                    if (inputRef.current) {
                        inputRef.current.focus();

                        // 커서를 텍스트 끝으로 이동
                        const selection = window.getSelection();
                        const range = document.createRange();

                        if (
                            selection &&
                            inputRef.current.childNodes.length > 0
                        ) {
                            const lastNode = inputRef.current.lastChild;
                            range.setStartAfter(lastNode);
                            range.collapse(true);
                            selection.removeAllRanges();
                            selection.addRange(range);
                        } else if (selection) {
                            // 내용이 없는 경우
                            range.selectNodeContents(inputRef.current);
                            range.collapse(false);
                            selection.removeAllRanges();
                            selection.addRange(range);
                        }

                        console.log('[TextInput] 입력 필드에 포커스 설정됨');
                    }
                }, 100);
            }
        };

        // WebSocket 응답 완료 이벤트 핸들러 추가
        const handleWebSocketResponseComplete = () => {
            console.log('[TextInput] WebSocket 응답 완료 이벤트 수신');
            setIsSending(false);

            // 입력 필드에 포커스 설정
            setTimeout(() => {
                if (inputRef.current) {
                    inputRef.current.focus();
                    console.log(
                        '[TextInput] WebSocket 응답 완료 후 입력 필드에 포커스 설정됨'
                    );
                }
            }, 100);
        };

        // 이벤트 리스너 등록
        window.addEventListener(
            'ai-loading-state-changed',
            handleLoadingStateChanged
        );
        window.addEventListener(
            'websocket-response-complete',
            handleWebSocketResponseComplete
        );

        // 컴포넌트 언마운트 시 이벤트 리스너 제거
        return () => {
            window.removeEventListener(
                'ai-loading-state-changed',
                handleLoadingStateChanged
            );
            window.removeEventListener(
                'websocket-response-complete',
                handleWebSocketResponseComplete
            );
        };
    }, []);

    // 컴포넌트 마운트 후 입력 필드에 포커스 설정 및 플레이스홀더 콜백 등록
    useEffect(() => {
        if (inputRef.current) {
            inputRef.current.focus();
            // console.log('[TextInput] 초기 포커스 설정됨');
        }

        // 플레이스홀더 설정 콜백 등록
        if (onSetPlaceholder) {
            onSetPlaceholder((newPlaceholder: string) => {
                setCurrentPlaceholder(newPlaceholder);
                console.log('[TextInput] 플레이스홀더 설정:', newPlaceholder);
            });
        }
    }, [onSetPlaceholder]);

    // placeholder 값이 변경되면 currentPlaceholder 업데이트
    useEffect(() => {
        // placeholder가 존재하면 사용, 아니면 기본값 유지
        const newPlaceholder = placeholder || defaultPlaceholder;
        setCurrentPlaceholder(newPlaceholder);
        console.log('[TextInput] 플레이스홀더 설정 변경:', newPlaceholder);

        // 입력 요소에 데이터 속성 직접 설정
        if (inputRef.current) {
            inputRef.current.setAttribute('data-placeholder', newPlaceholder);
            console.log('[TextInput] DOM 속성 직접 설정:', newPlaceholder);
        }
    }, [placeholder, defaultPlaceholder]);

    // 클릭 시 포커스 설정
    const handleContainerClick = () => {
        if (inputRef.current && !isLoading && !isSending) {
            inputRef.current.focus();
            console.log('[TextInput] 클릭으로 포커스 설정됨');
        }
    };

    // 초기 렌더링 또는 외부에서 값이 변경될 때만 DOM 업데이트
    useEffect(() => {
        if (
            inputRef.current &&
            value !== prevValue &&
            document.activeElement !== inputRef.current
        ) {
            console.log('[TextInput] 외부에서 입력값 변경:', value);

            // 텍스트만 설정하고 스타일링 적용
            inputRef.current.textContent = value;
            setPrevValue(value);

            // 해시태그 스타일링 적용
            setTimeout(() => {
                applyHashtagStyling();
            }, 10);
        }
    }, [value, prevValue, applyHashtagStyling]);

    const handleInputChange = () => {
        if (inputRef.current && !isComposing) {
            // 입력 내용 가져오기
            const newValue = inputRef.current.textContent || '';
            // console.log('[TextInput] 입력 변경:', JSON.stringify(newValue));

            // 공백 처리 확인
            const hasConsecutiveSpaces = newValue.includes('  ');
            if (hasConsecutiveSpaces) {
                console.log('[TextInput] 연속된 공백 감지됨');
            }

            // 상태 업데이트
            setPrevValue(newValue);
            onChange(newValue);

            // 해시태그 스타일링 적용 (약간 지연시켜 입력 완료 후 처리)
            setTimeout(() => {
                // console.log('[TextInput] 스타일링 적용 시도');
                applyHashtagStyling();
            }, 10);
        }
    };

    // DOM에서 최신 텍스트 내용을 가져오는 함수 추가
    const getLatestInputValue = (): string => {
        if (inputRef.current) {
            const latestText = inputRef.current.textContent || '';
            return latestText;
        }
        return value;
    };

    // 텍스트 필드에 실제 텍스트가 있는지 확인하는 함수
    const hasRealTextContent = (): boolean => {
        const latestText = getLatestInputValue();
        return latestText.trim() !== '';
    };

    const handleKeyPress = (e: KeyboardEvent<HTMLDivElement>) => {
        if (
            e.key === 'Enter' &&
            !e.shiftKey &&
            !isLoading &&
            !isSending &&
            !isComposing
        ) {
            e.preventDefault();

            // 엔터 키 누를 때 DOM에서 최신 내용 가져오기
            const latestValue = getLatestInputValue();

            // 값이 변경되었다면 상태 업데이트
            if (latestValue !== value && latestValue.trim() !== '') {
                setPrevValue(latestValue);
                onChange(latestValue);
                console.log(
                    '[TextInput] 엔터 키: 최신 내용으로 업데이트:',
                    latestValue
                );
            }

            // 약간의 지연 후 메시지 전송 (DOM 업데이트 보장)
            setTimeout(() => {
                if (latestValue.trim() !== '') {
                    handleSendMessage(latestValue);
                }
            }, 10);
        }
    };

    // 도구 사용 메시지 처리 로직은 AiRequest.tsx로 이동

    // 메시지 전송 처리 (매개변수로 최신 내용을 받을 수 있도록 수정)
    const handleSendMessage = (latestValue?: string) => {
        // 매개변수로 전달된 최신 값 또는 현재 상태 값 사용
        const messageToSend = latestValue || value;

        if (messageToSend.trim() === '' || isLoading || isSending) return;

        console.log(`[TextInput] 메시지 전송 시작: "${messageToSend}"`);
        console.log(`[TextInput] 사용자 ID: ${user?.id || '없음'}`);
        console.log(`[TextInput] 사용자 이름: ${user?.name || '없음'}`);

        // 도구 목록 초기화
        setActiveTools([]);
        setIsSending(true);

        try {
            // 상위 컴포넌트에 메시지 전달 (ChatWindow에서 API 요청 처리)
            console.log(
                '[TextInput] onSend 콜백 호출 - 전달할 메시지:',
                messageToSend
            );
            onSend(messageToSend);

            // 메시지 전송 후 입력 필드 비우기
            if (inputRef.current) {
                inputRef.current.textContent = '';
                setPrevValue('');
                onChange('');
                console.log('[TextInput] 메시지 전송 후 입력 필드 비움');
            }

            // 웹소켓 응답 완료 이벤트는 ChatWindow에서 API 요청 처리 후 발생할 것이므로 여기서는 제거
        } catch (error) {
            console.error('[TextInput] 메시지 전송 실패:', error);

            // 오류 메시지를 사용자에게 표시 (필요한 경우)
            if (onMessageReceived) {
                const errorMessage =
                    error instanceof Error
                        ? error.message
                        : t('textInput.errorMessage');

                // 서버 연결 오류 메시지 전달
                onMessageReceived(`⚠️ ${errorMessage}`, true);
            }

            setIsSending(false);
        } finally {
            // ChatWindow에서 응답 처리를 담당하므로 여기서는 UI 상태만 관리
            // 입력창은 ChatWindow에서 웹소켓 응답 완료 이벤트 발생 시 활성화됨
        }
    };

    // IME 이벤트 핸들러
    const handleCompositionStart = () => {
        setIsComposing(true);
        // console.log('[TextInput] 한글 조합 시작');
    };

    const handleCompositionEnd = () => {
        setIsComposing(false);
        // console.log('[TextInput] 한글 조합 종료');
        handleInputChange();
    };

    return (
        <Flex direction="column" style={{ width: '100%' }}>
            <Flex
                p="xs"
                align="center"
                style={{ borderTop: '1px solid #eaeaea' }}
            >
                <Box
                    className={classes.inputContainer}
                    onClick={handleContainerClick}
                    style={{ flexShrink: 0 }}
                >
                    <div
                        ref={inputRef}
                        contentEditable={!isLoading && !isSending}
                        className={classes.chatInput}
                        data-placeholder={currentPlaceholder || defaultPlaceholder}
                        onInput={handleInputChange}
                        onKeyDown={handleKeyPress}
                        onCompositionStart={handleCompositionStart}
                        onCompositionEnd={handleCompositionEnd}
                        suppressContentEditableWarning={true}
                    />
                </Box>
                <Button
                    className={classes.sendButton}
                    ml="xs"
                    style={{
                        flexShrink: 0,
                        borderRadius: '8px',
                        height: '44px',
                        backgroundColor:
                            hasRealTextContent() && !isLoading && !isSending
                                ? mantineTheme.colors.blue[6]
                                : undefined,
                        color: mantineTheme.white,
                    }}
                    onClick={() => {
                        // 버튼 클릭 시에도 최신 DOM 내용 가져오기
                        const latestValue = getLatestInputValue();
                        handleSendMessage(latestValue);
                    }}
                    loading={isSending}
                    disabled={!hasRealTextContent() || isLoading || isSending}
                >
                    {t('textInput.send')}
                </Button>
            </Flex>
        </Flex>
    );
};
export default ChatTextInput;
